import { useSyncExternalStore } from 'react';

type TListener = () => void;

export interface IAdminPageTitleOverride {
  pathname: string;
  title: string;
}

let current: IAdminPageTitleOverride | null = null;
const listeners = new Set<TListener>();

/**
 * Сравнивает два переопределения заголовка на равенство по значению —
 * нужно, чтобы повторный вызов с теми же `title`/`pathname` не будил подписчиков.
 * @param a Текущее значение стора
 * @param b Новое значение
 */
const isSameOverride = (
  a: IAdminPageTitleOverride | null,
  b: IAdminPageTitleOverride | null
) => {
  if (a === b) return true;
  if (!a || !b) return false;

  return a.title === b.title && a.pathname === b.pathname;
};

/**
 * Задаёт переопределение заголовка страницы админки (крошка в топбаре) —
 * для карточек generic-ресурсов, у которых `getRouteTitle` знает только
 * статический фоллбек (напр. «Фильм»), а настоящее имя приходит вместе с
 * записью. `null` снимает переопределение и возвращает статический заголовок.
 *
 * `pathname` — путь, для которого действует переопределение: топбар
 * показывает его только пока сам стоит на этом же пути (см. `AdminTopbar.tsx`)
 * — без этого сброс заголовка на смену маршрута зависел бы от порядка
 * эффектов `AdminTopbar`/детальной страницы, а не был бы гарантирован (19058,
 * волна 3, fix-review F9).
 * @param title Заголовок записи или null
 * @param pathname Путь, к которому относится переопределение
 */
export const setAdminPageTitle = (title: string | null, pathname: string) => {
  const next: IAdminPageTitleOverride | null = title
    ? { pathname, title }
    : null;

  if (isSameOverride(current, next)) return;

  current = next;
  listeners.forEach(listener => listener());
};

/**
 * Подписка стора на изменения (`useSyncExternalStore`).
 * @param listener Колбэк, вызываемый при каждом изменении заголовка
 */
const subscribe = (listener: TListener) => {
  listeners.add(listener);

  return () => listeners.delete(listener);
};

/**
 * Текущее значение стора.
 */
const getSnapshot = () => current;

/**
 * Читает текущее переопределение заголовка страницы админки (пара
 * `{ title, pathname }`), если оно есть. Топбар показывает `title` вместо
 * `getRouteTitle(pathname)` только пока его собственный `pathname` совпадает
 * с переопределением.
 */
export const useAdminPageTitle = () =>
  useSyncExternalStore(subscribe, getSnapshot);
